import { Link } from 'react-router-dom';
import { DEFAULT_LANGUAGE } from 'config/languages';
import { formatDate } from 'lib/dateHelpers';
import { getTranslation } from 'lib/dataTransform';
import DynamicStageChip from 'lib/components/dynamic-stage-chip/DynamicStageChip';
import ErrorBag from 'lib/components/error-bag/ErrorBag';
import ProtectedComponent from 'lib/components/protected-component/ProtectedComponent';
import { stageProgress } from 'lib/dynamicModificationStages';
import SearchInput from 'lib/components/search-input/SearchInput';
import Select from 'lib/components/select/Select';
import Table from 'lib/components/table/Table';
import { VIEW_SETTINGS_ROLES } from 'modules/settings/settingsPermissions';
import useSettingsDynamicStages from '../../useSettingsDynamicStages';
import SettingsDynamicRulesNav from '../settings-dynamic-rules-nav/SettingsDynamicRulesNav';

import '../settings-dynamic-rules-list-page/SettingsDynamicRulesListPage.scss';
import './SettingsDynamicStagesPage.scss';

export default function SettingsDynamicStagesPage(props) {
  const vm = useSettingsDynamicStages(props);

  const columns = [
    {
      title: 'Product',
      render: (level) => (
        <div className='stacked-cell'>
          <span>{level.assetExternalId || '-'}</span>
          {level.assetName && (
            <small className='truncated' title={level.assetName}>
              {level.assetName}
            </small>
          )}
        </div>
      ),
    },
    {
      title: 'Factory',
      render: (level) => (
        <div className='stacked-cell'>
          <span>{level.locationName || level.sourceName || '-'}</span>
          {level.locationExternalId && <small>{level.locationExternalId}</small>}
        </div>
      ),
    },
    {
      title: 'Question',
      headerClassName: 'question-cell',
      className: 'question-cell',
      render: (level) => {
        const question = getTranslation(level.questionName || [], DEFAULT_LANGUAGE).display;
        return (
          <div className='stacked-cell'>
            <span className='question-text' title={question}>
              {question}
            </span>
            <small>{level.ruleName}</small>
          </div>
        );
      },
    },
    {
      title: 'Stage now',
      render: (level) => (
        <div className='stacked-cell'>
          <DynamicStageChip stage={level.stage} title={level.ruleName} />
          <small data-testid='SettingsDynamicStagesPage.progress'>{stageProgress(level)}</small>
        </div>
      ),
    },
    {
      title: 'Last inspected',
      render: (level) =>
        level.lastInspectionId ? (
          <Link to={`/inspections/${level.lastInspectionId}/results`}>
            {formatDate(level.lastInspectedAt)}
          </Link>
        ) : (
          '-'
        ),
    },
    {
      title: '',
      className: 'actions-cell',
      render: (level) => (
        <ProtectedComponent allowed={VIEW_SETTINGS_ROLES}>
          <span
            className='link-button'
            data-testid='SettingsDynamicStagesPage.reset'
            onClick={() => vm.userResetsStage(level)}
          >
            Reset
          </span>
        </ProtectedComponent>
      ),
    },
  ];

  return (
    <div className='settings-dynamic-rules-page settings-dynamic-stages-page'>
      <div className='settings-header'>
        <h4>Dynamic modification rules</h4>
      </div>
      <SettingsDynamicRulesNav />
      <p className='form-info'>
        Where each question stands for each product and factory. The next
        inspection of that product at that factory asks the question at this
        stage. Reset sends it back to the rule's starting stage.
      </p>
      <div className='row dynamic-rules-filter-container'>
        <div className='search-container'>
          <SearchInput
            value={vm.state.search}
            onChange={vm.setSearch}
            placeholder='Product or factory'
            data-testid='SettingsDynamicStagesPage.search'
          />
        </div>
        <Select
          className='rule-filter'
          {...vm.ruleFilterProps}
          data-testid='SettingsDynamicStagesPage.ruleFilter'
        />
      </div>
      <ErrorBag errors={vm.state.errors} />
      <Table
        columns={columns}
        data={vm.state.data}
        className='settings-dynamic-stages-table'
        isLoading={vm.state.loading}
        page={vm.state.page}
        pageSize={vm.state.pageSize}
        count={vm.state.count}
        setPage={vm.setPage}
        setPageSize={vm.setPageSize}
        emptyState={
          <p className='empty-state'>
            Nothing yet. A row appears once an inspection with a rule-bearing
            question is finished.
          </p>
        }
      />
    </div>
  );
}
